import { useState, useEffect } from "react";
import api from "../../../../utils/axiosInstance";
import FormInput from "../../../../components/Form/FormInput";
import FormSelect from "../../../../components/Form/FormSelect";
import CheckboxField from "../../../../components/Form/CheckboxField";
import { getTimezonesWithOffset } from "../../../../utils/constants/timezones";

const staticTimezones = getTimezonesWithOffset();

const genders = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Other", value: "other" },
];

const ManagerEditForm = ({ manager, id, onCancel, refreshManager }) => {
  const [formData, setFormData] = useState({
    first_name: manager.first_name || "",
    last_name: manager.last_name || "",
    role_id: manager.role?.role_id?.toString() || "",
    client_pool_id: manager.client_pool?.id?.toString() || "",
    gender: manager.gender || "",
    email: manager.auth?.email || "",
    phone: manager.phone || "",
    teams_id: manager.teams_id || "",
    zoom_id: manager.zoom_id || "",
    timezone: manager.timezone || "",
    can_login: !!manager.can_login,
  });

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [clientPools, setClientPools] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get("/manager-roles", { withCredentials: true });
        setRoles(res.data.data);
      } catch (err) {
        console.error("Failed to fetch roles:", err.response || err);
      }
    };

    const fetchClientPools = async () => {
      try {
        const res = await api.get("/client-pools", { withCredentials: true });
        setClientPools(res.data.data);
      } catch (err) {
        console.error("Failed to fetch client pools:", err.response || err);
      }
    };

    fetchRoles();
    fetchClientPools();
  }, []);

  const requiredFields = ["first_name", "last_name", "role_id", "email"];

  const validate = () => {
    const currentErrors = {};
    if (!formData.first_name) currentErrors.first_name = "First Name is required.";
    if (!formData.last_name) currentErrors.last_name = "Last Name is required.";
    if (!formData.role_id) currentErrors.role_id = "Role is required.";
    if (!formData.email) currentErrors.email = "Email is required.";
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let errorMsg = "";
    if (requiredFields.includes(name) && !value) {
      errorMsg =
        name === "email"
          ? "Email is required."
          : name === "role_id"
          ? "Role is required."
          : `${name
              .replace("_", " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())} is required.`;
    }
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setFormError("");
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        role_id: Number(formData.role_id),
        client_pool_id: formData.client_pool_id
          ? Number(formData.client_pool_id)
          : null,
        gender: formData.gender || null,
        phone: formData.phone || null,
        teams_id: formData.teams_id || null,
        zoom_id: formData.zoom_id || null,
      };

      await api.put(`/managers/${id}`, payload, { withCredentials: true });
      await refreshManager();
      onCancel();
    } catch (err) {
      console.error(err);
      setFormError(
        err.response?.data?.message || "Failed to update manager"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-5"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
        <FormInput
          label="First Name"
          name="first_name"
          value={formData.first_name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.first_name}
          required
        />

        <FormInput
          label="Last Name"
          name="last_name"
          value={formData.last_name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.last_name}
          required
        />

        <FormSelect
          label="Role"
          name="role_id"
          value={formData.role_id}
          onChange={handleChange}
          onBlur={handleBlur}
          options={roles.map((role) => ({
            label: role.name,
            value: role.id.toString(),
          }))}
          error={errors.role_id}
          required
        />

        <FormSelect
          label="Client Pool"
          name="client_pool_id"
          value={formData.client_pool_id}
          onChange={handleChange}
          options={clientPools.map((pool) => ({
            value: pool.id.toString(),
            label: pool.name,
          }))}
        />

        <FormSelect
          label="Gender"
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          options={genders}
        />

        <FormInput
          label="Email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.email}
          type="email"
          required
        />

        <FormInput
          label="Phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
        />

        <FormInput
          label="Teams ID"
          name="teams_id"
          value={formData.teams_id}
          onChange={handleChange}
        />

        <FormInput
          label="Zoom ID"
          name="zoom_id"
          value={formData.zoom_id}
          onChange={handleChange}
        />

        <FormSelect
          label="Timezone"
          name="timezone"
          value={formData.timezone}
          onChange={handleChange}
          options={staticTimezones}
        />
      </div>

      <CheckboxField
        label="Can Login"
        name="can_login"
        checked={formData.can_login}
        onChange={handleChange}
      />

      {formError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
          {formError}
        </div>
      )}

      <div className="pt-3 border-t border-gray-100 space-y-3">
        <span className="block text-xs text-gray-500">
          Fields marked with <span className="text-red-500">*</span> are mandatory.
        </span>
        <div className="flex gap-2 text-sm">
          <button
            type="submit"
            disabled={submitting}
            className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default ManagerEditForm;
